/**
 * Service de gestion du chat en temps réel
 */

import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse, TokenStorage } from './apiConfig'

// Fonction pour gérer les réponses HTTP et les erreurs 401
async function handleHttpResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // Si erreur 401 (Non autorisé), déconnecter l'utilisateur
  if (response.status === 401) {
    console.error('[ChatService] ❌ Erreur 401 - Token invalide ou expiré')
    console.error('[ChatService] 🔄 Nettoyage du localStorage et redirection')
    
    // Nettoyer le stockage
    TokenStorage.clear()
    
    // Rediriger vers la page de connexion si on est dans le navigateur
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      window.location.href = `/auth/sign-in?redirect=${encodeURIComponent(currentPath)}`
    }
    
    return {
      status: 401,
      title: 'Non autorisé',
      message: 'Votre session a expiré. Veuillez vous reconnecter.'
    }
  }
  
  // Vérifier si la réponse est OK
  if (!response.ok) {
    try {
      const errorData = await response.json()
      return {
        status: errorData.status || response.status,
        title: errorData.title || 'Erreur',
        message: errorData.message || 'Une erreur est survenue',
        data: errorData.data
      }
    } catch (e) {
      return {
        status: response.status,
        title: 'Erreur',
        message: `Erreur HTTP ${response.status}: ${response.statusText}`
      }
    }
  }
  
  // Réponse OK, parser le JSON
  try {
    return await response.json()
  } catch (e) {
    return {
      status: 3,
      title: 'Erreur',
      message: 'Erreur lors de la lecture de la réponse du serveur'
    }
  }
}

export interface ChatConversation {
  id: number
  titre: string
  type: string
  agentNom: string
  agentAvatar: string
  dernierMessage: ChatMessage | null
  statut: string
  updated_at?: string
}

export interface ChatMessage {
  id: number
  conversationId: number
  texte: string
  metadata?: any
  agentNom?: string
  agentAvatar?: string
  date: string
  statut: 'sent' | 'delivered' | 'read'
  qui: 'user' | 'bot'
}

export interface SendMessageData {
  conversationId: number
  texte: string
  conversationType?: string
}

class ChatService {
  /**
   * Récupérer la liste des conversations de l'utilisateur
   */
  async getConversations(): Promise<ApiResponse<ChatConversation[]>> {
    try {
      console.log('[ChatService] 📥 Récupération des conversations')
      const response = await fetch(`${API_CONFIG.baseURL}/conversations`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      
      console.log('[ChatService] 📤 Réponse reçue:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })
      
      return await handleHttpResponse<ChatConversation[]>(response)
    } catch (error) {
      console.error('[ChatService] ❌ Erreur lors de la récupération des conversations:', error)
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les messages d'une conversation
   */
  async getMessages(conversationId: number): Promise<ApiResponse<ChatMessage[]>> {
    try {
      console.log('[ChatService] 📥 Récupération des messages pour la conversation:', conversationId)
      const response = await fetch(`${API_CONFIG.baseURL}/conversations/${conversationId}/messages`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      
      console.log('[ChatService] 📤 Réponse reçue:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })
      
      return await handleHttpResponse<ChatMessage[]>(response)
    } catch (error) {
      console.error('[ChatService] ❌ Erreur lors de la récupération des messages:', error)
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un message
   */
  async sendMessage(data: SendMessageData): Promise<ApiResponse<{ message: ChatMessage, conversationId: number }>> {
    try {
      console.log('[ChatService] 📤 Envoi d\'un message:', data)
      const response = await fetch(`${API_CONFIG.baseURL}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      })
      
      console.log('[ChatService] 📥 Réponse reçue:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })
      
      return await handleHttpResponse<{ message: ChatMessage, conversationId: number }>(response)
    } catch (error) {
      console.error('[ChatService] ❌ Erreur lors de l\'envoi du message:', error)
      return handleApiError(error)
    }
  }

  /**
   * Créer une nouvelle conversation
   */
  async createConversation(type: string): Promise<ApiResponse<ChatConversation>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/conversations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ type })
      })
      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }
}

// Export d'une instance singleton
export default new ChatService()
